// ==========================================
// src/pages/CadastroEmpresa.jsx — V10 DEFINITIVO
// ✅ Usa garantirSessao() do supabase.js
// ✅ Restaura do sessionStorage se necessário
// ✅ NÃO redireciona para login indevidamente
// ✅ CNPJ limpo enviado para Supabase
// ==========================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, garantirSessao } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { 
  Building2, Mail, Phone, MapPin, FileText, 
  ArrowRight, Loader2, AlertCircle, CheckCircle, Search,
  Lock, Eye, EyeOff, User
} from 'lucide-react';
import { toast } from 'sonner';

// ==========================================
// FUNÇÕES UTILITÁRIAS
// ==========================================

const limparNumeros = (valor) => valor.replace(/\D/g, '');

const formatarCNPJ = (cnpj) => {
  const numeros = limparNumeros(cnpj);
  if (numeros.length <= 2) return numeros;
  if (numeros.length <= 5) return `${numeros.slice(0, 2)}.${numeros.slice(2)}`;
  if (numeros.length <= 8) return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5)}`;
  if (numeros.length <= 12) return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8)}`;
  return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-${numeros.slice(12, 14)}`;
};

const validarCNPJ = (cnpj) => {
  const cnpjLimpo = limparNumeros(cnpj);
  if (cnpjLimpo.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpjLimpo)) return false;
  
  let tamanho = cnpjLimpo.length - 2;
  let numeros = cnpjLimpo.substring(0, tamanho);
  let digitos = cnpjLimpo.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== Number(digitos.charAt(0))) return false;
  
  tamanho = tamanho + 1;
  numeros = cnpjLimpo.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (resultado !== Number(digitos.charAt(1))) return false;
  
  return true;
};

// ==========================================
// COMPONENTE
// ==========================================

export default function CadastroEmpresa() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cnpjValido, setCnpjValido] = useState(null);
  const [userId, setUserId] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [empresaExiste, setEmpresaExiste] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sessaoVerificada, setSessaoVerificada] = useState(false);
  
  const isSubmittingRef = useRef(false);
  const cepTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    telefone: '',
    celular: '',
    cep: '',
    endereco: '',
    cidade: '',
    estado: '',
    nome_responsavel: '',
    cargo_responsavel: '',
    site: '',
    senha: '',
    confirmarSenha: ''
  });

  // ==========================================
  // ✅ VERIFICA USUÁRIO LOGADO — USANDO garantirSessao()
  // ==========================================
  useEffect(() => {
    const getUser = async () => {
      console.log('[CadastroEmpresa] Verificando sessão...');
      
      // ✅ USA O HELPER UNIFICADO DO SUPABASE
      const { user, session, error } = await garantirSessao();

      if (!user) {
        console.error('❌ [CadastroEmpresa] Sem sessão:', error);
        toast.error('Sessão expirada. Faça login novamente.');
        // Delay para não quebrar a renderização
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      console.log('✅ [CadastroEmpresa] Sessão ativa:', user.id);
      setUserId(user.id);
      setAuthEmail(user.email);
      setSessaoVerificada(true);

      // Verifica se já existe empresa
      try {
        const { data: empresa } = await supabase
          .from('empresas')
          .select('*')
          .eq('usuario_id', user.id)
          .maybeSingle();

        if (empresa) {
          setEmpresaExiste(true);
          setFormData(prev => ({
            ...prev,
            razao_social: empresa.razao_social || '',
            nome_fantasia: empresa.nome_fantasia || '',
            cnpj: empresa.cnpj ? formatarCNPJ(empresa.cnpj) : '',
            telefone: empresa.telefone || '',
            celular: empresa.celular || '',
            cep: empresa.cep || '',
            endereco: empresa.endereco || '',
            cidade: empresa.cidade || '',
            estado: empresa.estado || '',
            nome_responsavel: empresa.nome_responsavel || '',
            cargo_responsavel: empresa.cargo_responsavel || '',
            site: empresa.site || ''
          }));
          if (empresa.cnpj) setCnpjValido(true);
        }
      } catch (err) {
        console.error('Erro ao buscar empresa:', err);
      }
    };

    getUser();
  }, []);

  useEffect(() => {
    return () => {
      if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
    };
  }, []);

  // ==========================================
  // BUSCA CEP AUTOMÁTICO
  // ==========================================
  const buscarCep = async (cep) => {
    const cepLimpo = limparNumeros(cep);
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado!');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));
      
      toast.success('Endereço preenchido automaticamente!');
    } catch (err) {
      console.error('Erro CEP:', err);
      toast.error('Erro ao buscar CEP');
    } finally {
      setBuscandoCep(false);
    }
  };

  // ==========================================
  // HANDLE CHANGE COM MÁSCARAS
  // ==========================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cnpj') {
      const numeros = limparNumeros(value);
      if (numeros.length > 14) return;
      
      setFormData(prev => ({ ...prev, [name]: formatarCNPJ(numeros) }));
      
      if (numeros.length === 14) {
        setCnpjValido(validarCNPJ(numeros));
      } else {
        setCnpjValido(null);
      }
      
    } else if (name === 'cep') {
      const numeros = limparNumeros(value);
      const formatado = numeros.replace(/^(\d{5})(\d)/, '$1-$2');
      setFormData(prev => ({ ...prev, [name]: formatado }));
      
      if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
      if (numeros.length === 8) {
        cepTimerRef.current = setTimeout(() => buscarCep(formatado), 500);
      }
      
    } else if (name === 'telefone' || name === 'celular') {
      const numeros = limparNumeros(value);
      let formatado = numeros;
      if (numeros.length > 2) formatado = `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
      if (numeros.length > 7) formatado = `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
      setFormData(prev => ({ ...prev, [name]: formatado }));
      
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ==========================================
  // SUBMIT — V10 COM garantirSessao()
  // ==========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // ✅ GARANTE SESSÃO NO SUBMIT
      const { user: currentUser, error: sessaoError } = await garantirSessao();

      if (!currentUser) {
        toast.error('Erro de autenticação. Faça login novamente.');
        window.location.href = '/login';
        return;
      }

      // --- Valida CNPJ ---
      const cnpjLimpo = limparNumeros(formData.cnpj);
      
      if (cnpjLimpo.length !== 14 || !validarCNPJ(cnpjLimpo)) {
        toast.error('CNPJ inválido! Verifique os dígitos.');
        return;
      }

      // Verifica duplicidade CNPJ
      const { data: cnpjExistente, error: cnpjCheckError } = await supabase
        .from('empresas')
        .select('id')
        .eq('cnpj', cnpjLimpo)
        .neq('usuario_id', currentUser.id)
        .maybeSingle();

      if (cnpjCheckError) {
        console.error('Erro ao verificar CNPJ:', cnpjCheckError);
      }

      if (cnpjExistente) {
        toast.error('Este CNPJ já está cadastrado em outra conta!');
        return;
      }

      // --- Valida Senha ---
      if (!empresaExiste) {
        if (!formData.senha || formData.senha.length < 6) {
          toast.error('Senha deve ter no mínimo 6 dígitos');
          return;
        }
        if (formData.senha !== formData.confirmarSenha) {
          toast.error('As senhas não coincidem');
          return;
        }
      }

      // --- Atualiza senha no Supabase Auth ---
      if (!empresaExiste && formData.senha) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: formData.senha
        });
        if (passwordError) {
          console.error('Erro ao definir senha:', passwordError);
          toast.error('Erro ao definir senha: ' + passwordError.message);
          return;
        }
        console.log('✅ Senha definida com sucesso!');
      }

      // --- PREPARA DADOS ---
      const dadosEmpresa = {
        usuario_id: currentUser.id,
        razao_social: formData.razao_social.trim(),
        nome_fantasia: formData.nome_fantasia.trim() || null,
        cnpj: cnpjLimpo,
        telefone: limparNumeros(formData.telefone) || null,
        celular: limparNumeros(formData.celular) || null,
        email: authEmail,
        site: formData.site.trim() || null,
        cep: limparNumeros(formData.cep) || null,
        endereco: formData.endereco.trim() || null,
        cidade: formData.cidade.trim() || null,
        estado: formData.estado.trim().toUpperCase() || null,
        nome_responsavel: formData.nome_responsavel.trim() || null,
        cargo_responsavel: formData.cargo_responsavel.trim() || null,
        status: 'em_analise',
        updated_at: new Date().toISOString()
      };

      console.log('📤 Enviando para empresas:', dadosEmpresa);

      // Upsert simples
      const { error: upsertError } = await supabase
        .from('empresas')
        .upsert(dadosEmpresa, { onConflict: 'usuario_id' });

      if (upsertError) {
        console.error('❌ Erro upsert:', upsertError);
        
        if (upsertError.message?.includes('cnpj') && upsertError.code === '23505') {
          toast.error('Este CNPJ já está cadastrado!');
        } else {
          toast.error('Erro ao salvar: ' + upsertError.message);
        }
        return;
      }

      console.log('✅ Empresa salva! CNPJ:', cnpjLimpo);
      toast.success('Cadastro enviado! Aguardando aprovação do admin.');
      
      await refreshUser();
      navigate('/perfil');

    } catch (err) {
      console.error('❌ Erro geral:', err);
      toast.error('Erro inesperado. Tente novamente.');
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  
  // Loading inicial enquanto verifica sessão
  if (!sessaoVerificada && !authEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {empresaExiste ? 'Completar Cadastro' : 'Cadastro de Empresa'}
          </h1>
          <p className="text-slate-400">Complete seus dados para publicar cargas</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 space-y-6">
          
          {/* Razão Social */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Razão Social *</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="razao_social"
                value={formData.razao_social}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Razão Social da empresa"
              />
            </div>
          </div>

          {/* Nome Fantasia */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nome Fantasia</label>
            <input
              type="text"
              name="nome_fantasia"
              value={formData.nome_fantasia}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Nome Fantasia (opcional)"
            />
          </div>

          {/* CNPJ com validação visual */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">CNPJ *</label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                required
                maxLength={18}
                className={`w-full pl-10 pr-12 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-400 focus:ring-2 outline-none ${
                  cnpjValido === true ? 'border-green-500 focus:ring-green-500' : 
                  cnpjValido === false ? 'border-red-500 focus:ring-red-500' : 
                  'border-slate-600 focus:ring-green-500'
                }`}
                placeholder="00.000.000/0000-00"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {cnpjValido === true && <CheckCircle className="w-5 h-5 text-green-400" />}
                {cnpjValido === false && <AlertCircle className="w-5 h-5 text-red-400" />}
              </div>
            </div>
            {cnpjValido === false && (
              <p className="text-red-400 text-sm mt-1">CNPJ inválido! Verifique os dígitos.</p>
            )}
          </div>

          {/* Email (readonly - do authUser) */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="email"
                value={authEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-700/30 border border-slate-600 rounded-lg text-slate-400 cursor-not-allowed"
                placeholder="email@exemplo.com"
              />
            </div>
            <p className="text-slate-500 text-xs mt-1">Email vinculado à conta Google</p>
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Telefone *</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="(11) 99999-9999"
              />
            </div>
          </div>

          {/* Celular */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Celular / WhatsApp</label>
            <input
              type="tel"
              name="celular"
              value={formData.celular}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="(11) 99999-9999 (opcional)"
            />
          </div>

          {/* CEP com busca automática */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">CEP</label>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${buscandoCep ? 'text-blue-400 animate-pulse' : 'text-slate-400'}`} />
              <input
                type="text"
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                maxLength={9}
                className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="00000-000"
              />
              {buscandoCep && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Endereço */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Endereço</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Rua, número, bairro"
              />
            </div>
          </div>

          {/* Cidade + Estado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Cidade</label>
              <input
                type="text"
                name="cidade"
                value={formData.cidade}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Cidade"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Estado</label>
              <input
                type="text"
                name="estado"
                value={formData.estado}
                onChange={handleChange}
                maxLength={2}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none uppercase"
                placeholder="SP"
              />
            </div>
          </div>

          {/* Nome Responsável */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nome do Responsável</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                name="nome_responsavel"
                value={formData.nome_responsavel}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                placeholder="Nome do responsável legal"
              />
            </div>
          </div>

          {/* Cargo Responsável */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Cargo do Responsável</label>
            <input
              type="text"
              name="cargo_responsavel"
              value={formData.cargo_responsavel}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="Ex: Diretor, Gerente, etc."
            />
          </div>

          {/* Site */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Site</label>
            <input
              type="url"
              name="site"
              value={formData.site}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
              placeholder="https://www.suaempresa.com"
            />
          </div>

          {/* SENHA (só aparece se for novo cadastro) */}
          {!empresaExiste && (
            <>
              <div className="border-t border-slate-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-green-400" />
                  Criar Senha de Acesso
                </h3>
                
                {/* Senha */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Senha * (mínimo 6 dígitos)</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="senha"
                      value={formData.senha}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="Mínimo 6 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirmar Senha */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Confirmar Senha *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmarSenha"
                      value={formData.confirmarSenha}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 outline-none"
                      placeholder="Repita a senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Botão */}
          <button
            type="submit"
            disabled={loading || cnpjValido !== true}
            className="w-full py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Salvando...</>
            ) : (
              <><CheckCircle className="w-5 h-5" /> {empresaExiste ? 'Atualizar Cadastro' : 'Finalizar Cadastro'}</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}